import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock, Download, Loader2 } from 'lucide-react';
import { useSyncStatus } from '../../hooks/useCostAccounting.js';
import { costAccountingApi } from '../../lib/cost-accounting-api.js';
import { downloadCsv, escapeCell } from '../../lib/csv.js';
import type { PipelineStep } from '../../types/cost-accounting.js';

/** @graph-connects none */
function yen(amount: number): string {
  return `¥${amount.toLocaleString('ja-JP')}`;
}

/** @graph-connects none */
function SyncStatusSection({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useSyncStatus(enabled);

  if (isLoading) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-base)] p-4">
        <div className="flex items-center gap-2 text-sm font-bold">
          <Loader2 className="h-4 w-4 animate-spin text-[#1d4ed8]" aria-hidden="true" />
          {t('costAccounting.syncStatusLoading')}
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-base)] p-4">
        <div className="text-sm text-[var(--color-text-secondary)]">
          {t('costAccounting.syncStatusError')}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-base)] p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold">
        <Clock className="h-4 w-4 text-[#1d4ed8]" aria-hidden="true" />
        {t('costAccounting.syncStatus')}
        <span
          className="rounded px-2 py-0.5 text-[0.625rem] font-bold"
          style={{
            background: data.allOk ? '#f0fdf4' : '#fef2f2',
            color: data.allOk ? '#16a34a' : '#dc2626',
          }}
        >
          {data.allOk ? t('costAccounting.allTablesOk') : t('costAccounting.needsCheck')}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[0.6875rem]">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="py-1.5 pr-3 text-left font-semibold text-[var(--color-text-muted)]">
                {t('costAccounting.table')}
              </th>
              <th className="py-1.5 px-3 text-right font-semibold text-[var(--color-text-muted)]">
                {t('costAccounting.bqRowCount')}
              </th>
              <th className="py-1.5 px-3 text-left font-semibold text-[var(--color-text-muted)]">
                {t('costAccounting.bqLastUpdated')}
              </th>
              <th className="py-1.5 px-3 text-left font-semibold text-[var(--color-text-muted)]">
                {t('costAccounting.freshness')}
              </th>
              <th className="py-1.5 pl-3 text-left font-semibold text-[var(--color-text-muted)]">
                {t('costAccounting.verdict')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.entries.map((e) => (
              <tr key={e.tableName} className="border-b border-[var(--color-border)]">
                <td className="py-1.5 pr-3">{e.tableName}</td>
                <td className="py-1.5 px-3 text-right tabular-nums font-semibold">
                  {e.rowCount.toLocaleString('ja-JP')}
                </td>
                <td className="py-1.5 px-3 tabular-nums">{e.lastModified}</td>
                <td className="py-1.5 px-3">
                  <span style={{ color: e.ok ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                    {t('costAccounting.hoursAgo', { hours: String(e.hoursAgo) })}
                  </span>
                </td>
                <td className="py-1.5 pl-3">
                  <span style={{ color: e.ok ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                    {e.ok ? 'OK' : 'NG'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[0.6875rem] text-[var(--color-text-muted)]">
        {t('costAccounting.mirrorLastCheck')}{' '}
        <strong style={{ color: '#16a34a' }}>
          {data.checkedAt.slice(0, 19).replace('T', ' ')}
        </strong>{' '}
        {t('costAccounting.mirrorFreshnessRule')}
      </div>
    </div>
  );
}

/** @graph-connects none */
function PipelineSection({ pipelineSteps }: { pipelineSteps: PipelineStep[] }) {
  const { t } = useTranslation();
  return (
    <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-base)] p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold">
        <CheckCircle2 className="h-4 w-4 text-[#475569]" aria-hidden="true" />
        {t('costAccounting.computePipeline')}
      </div>
      <div className="flex flex-col">
        {pipelineSteps.map((step, idx) => (
          <div key={step.step}>
            <div className="grid grid-cols-[44px_1fr_auto] items-start gap-3">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#1d4ed8] text-xs font-bold text-white">
                {String(step.step)}
              </div>
              <div>
                <div className="text-xs font-semibold text-[var(--color-text-secondary)]">
                  {step.label}
                </div>
                <div className="mt-0.5 text-[0.6875rem] text-[var(--color-text-muted)]">
                  {step.detail}
                </div>
              </div>
              <div className="text-right">
                {step.count !== null && (
                  <div className="text-xs font-semibold tabular-nums">
                    {step.count.toLocaleString('ja-JP')}件
                  </div>
                )}
                {step.amount !== null && (
                  <div className="text-xs font-semibold tabular-nums">{yen(step.amount)}</div>
                )}
                {step.subItems.map((sub) => (
                  <div
                    key={sub.label}
                    className="text-[0.625rem] tabular-nums text-[var(--color-text-muted)]"
                  >
                    {sub.label}: {sub.value}
                  </div>
                ))}
              </div>
            </div>
            {idx < pipelineSteps.length - 1 && (
              <div className="ml-[13px] flex justify-start py-0.5">
                <div className="h-4 w-0.5 bg-[var(--color-border)]" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** @graph-connects none */
function RawDataDownloadSection({ month }: { month: string }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<string | null>(null);

  const handleDownload = async (type: 'items' | 'purchased' | 'unknown'): Promise<void> => {
    setLoading(type);
    try {
      const result = await costAccountingApi.rawData(type);
      const rows = result.rows as Record<string, unknown>[];
      if (rows.length === 0) {
        return;
      }
      const headers = Object.keys(rows[0] ?? {});
      const csvLines = [
        headers.map(escapeCell).join(','),
        ...rows.map((row) =>
          headers
            .map((h) => {
              const val = row[h] as string | number | boolean | null | undefined;
              return escapeCell(val === null || val === undefined ? '' : String(val));
            })
            .join(','),
        ),
      ];
      const BOM = '﻿';
      downloadCsv(`raw_${type}_${month}.csv`, `${BOM}${csvLines.join('\r\n')}`);
    } finally {
      setLoading(null);
    }
  };

  const buttons: { type: 'items' | 'purchased' | 'unknown'; label: string; sub: string }[] = [
    { type: 'items', label: t('costAccounting.rawItems'), sub: t('costAccounting.rawItemsSub') },
    { type: 'purchased', label: t('costAccounting.rawPurchased'), sub: 'is_purchased=1' },
    {
      type: 'unknown',
      label: t('costAccounting.rawUnknown'),
      sub: t('costAccounting.rawUnknownSub'),
    },
  ];

  return (
    <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-base)] p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold">
        <Download className="h-4 w-4 text-[var(--color-text-secondary)]" aria-hidden="true" />
        {t('costAccounting.rawDownload')}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {buttons.map((btn) => (
          <button
            key={btn.type}
            type="button"
            onClick={() => {
              void handleDownload(btn.type);
            }}
            disabled={loading !== null}
            className="flex flex-col items-center gap-1.5 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-base)] p-4 transition-colors hover:border-[#1d4ed8] disabled:opacity-50"
          >
            {loading === btn.type ? (
              <Loader2 className="h-5 w-5 animate-spin text-[#1d4ed8]" aria-hidden="true" />
            ) : (
              <Download className="h-5 w-5 text-[var(--color-text-muted)]" aria-hidden="true" />
            )}
            <span className="text-xs font-semibold">{btn.label}</span>
            <span className="text-[0.625rem] text-[var(--color-text-muted)]">{btn.sub}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business Data validation tab showing BQ sync status, computation pipeline visualization, and raw data CSV download buttons.
 * @graph-connects useSyncStatus [calls] BQ sync status hook
 * @graph-connects costAccountingApi [calls] Raw data download
 * @graph-connects downloadCsv [calls] CSV download utility
 */
export function CostAccountingDataValidation({
  pipelineSteps,
  month,
}: {
  pipelineSteps: PipelineStep[];
  month: string;
}) {
  return (
    <div className="mt-6">
      <SyncStatusSection enabled={true} />
      <PipelineSection pipelineSteps={pipelineSteps} />
      <RawDataDownloadSection month={month} />
    </div>
  );
}
